import { useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface Step {
  step: string;
  ok: boolean;
  detail?: string;
}

interface Result {
  ok: boolean;
  provider: string;
  endpoint: string;
  bucket: string;
  region: string;
  source: 'tenant' | 'env';
  steps: Step[];
}

interface Settings {
  endpoint: string | null;
  region: string | null;
  bucket: string | null;
  forcePathStyle: boolean | null;
  accessKeyRef: string | null;
  secretKeyRef: string | null;
  effective: {
    endpoint: string | null;
    region: string;
    bucket: string;
    forcePathStyle: boolean;
    source: 'tenant' | 'env';
    hasCredentials: boolean;
  };
}

type Form = {
  endpoint: string;
  region: string;
  bucket: string;
  forcePathStyle: boolean;
  accessKeyRef: string;
  secretKeyRef: string;
};

function toForm(s: Settings): Form {
  return {
    endpoint: s.endpoint ?? '',
    region: s.region ?? '',
    bucket: s.bucket ?? '',
    forcePathStyle: s.forcePathStyle ?? true,
    accessKeyRef: s.accessKeyRef ?? '',
    secretKeyRef: s.secretKeyRef ?? '',
  };
}

/**
 * 对象存储。
 *
 * ★ 地址、桶名、区域可以在这儿改 —— 和地理编码/短信/底图一套做法,
 *   换存储不用重新发版。
 *
 * ★ 但**密钥这一栏填的是引用**(env:变量名),不是密钥本身。
 *   那把钥匙能读写整桶的签收照片,而照片里有门牌号和收件人姓名 ——
 *   它一旦进了业务库,「数据库被导出一份」和「照片全泄露」
 *   就成了同一件事。所以真值留在服务器的环境变量里,这一屏只存名字。
 *
 * ★ 改完一定要跑一次自检。存储配错的表现是司机传不上照片,
 *   而司机端是离线优先的 —— 他点完就走,错误几分钟后才浮出来,
 *   最后在管理端看到的只是一句「操作还没同步」。
 */
export function StorageSettings({ canManage }: { canManage: boolean }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [res, setRes] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** 换桶要点第二次 —— 后端也会拦,这里只是把警告先说出来 */
  const [confirmBucket, setConfirmBucket] = useState(false);

  useEffect(() => {
    if (!canManage) return;
    void api<Settings>('/settings/storage')
      .then((s) => {
        setSettings(s);
        setForm(toForm(s));
      })
      .catch((e) => setErr(e instanceof ApiFailure ? e.message : '读不到当前配置'));
  }, [canManage]);

  async function run() {
    setBusy(true);
    setErr(null);
    setRes(null);
    try {
      setRes(await api<Result>('/settings/storage/test', { method: 'POST' }));
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '自检没跑起来');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      const next = await api<Settings>('/settings/storage', {
        method: 'PATCH',
        body: JSON.stringify({
          endpoint: form.endpoint.trim() || null,
          region: form.region.trim() || null,
          bucket: form.bucket.trim() || null,
          forcePathStyle: form.forcePathStyle,
          accessKeyRef: form.accessKeyRef.trim() || null,
          secretKeyRef: form.secretKeyRef.trim() || null,
          ...(confirmBucket ? { confirmBucketChange: true } : {}),
        }),
      });
      setSettings(next);
      setForm(toForm(next));
      setConfirmBucket(false);
      setSaved(true);
      // 改完立刻验一次 —— 存错了在这儿就知道,而不是等司机传不上照片
      void run();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '存不下来');
      // 后端拦的就是换桶,把二次确认亮出来
      if (e instanceof ApiFailure && e.message.includes('换桶')) setConfirmBucket(true);
    } finally {
      setSaving(false);
    }
  }

  const bucketChanged = Boolean(
    settings?.bucket && form && form.bucket.trim() !== settings.bucket,
  );
  const usingEnv = settings?.effective.source === 'env';

  const input =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 ' +
    'dark:bg-slate-800 dark:text-slate-100';

  return (
    <section aria-label="对象存储">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">对象存储</h2>
      <p className="mb-4 text-sm text-slate-500">
        司机拍的照片、导入导出的文件都存在这里。走的是 S3 协议 —— AWS S3、Cloudflare R2、MinIO
        都能接
      </p>

      {settings && (
        <div className="mb-4 max-w-2xl space-y-3">
          {/*
            ★ 先说清现在生效的是哪一套。留空时用的是部署环境里的配置,
              和后台配的长得一模一样,但改法完全不同(一个在这儿改,
              一个要改部署)—— 不说清楚的话,人会在这一屏上改半天
              却发现「怎么没变化」。
          */}
          <div
            className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800/60
                       dark:text-slate-300"
            data-testid="storage-source"
          >
            {usingEnv
              ? '现在用的是服务器环境变量里的配置(下面留空就是这个意思)。在这儿填了就以这儿为准'
              : '现在用的是这一屏配的。全部清空则回落到服务器环境变量'}
            <div className="mt-1 font-mono text-xs text-slate-500">
              {settings.effective.bucket} @ {settings.effective.endpoint ?? '(AWS 默认)'} ·{' '}
              {settings.effective.region} ·{' '}
              {settings.effective.hasCredentials ? '密钥已取到' : '密钥取不到 —— 传不上照片'}
            </div>
          </div>

          {form && (
            <div className="grid gap-3">
              <label className="block">
                <span className="mb-1 block text-sm text-slate-600 dark:text-slate-300">
                  服务地址
                </span>
                <input
                  className={input}
                  value={form.endpoint}
                  disabled={!canManage}
                  placeholder="https://<账号ID>.r2.cloudflarestorage.com(AWS 官方留空)"
                  onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-sm text-slate-600 dark:text-slate-300">桶</span>
                  <input
                    className={input}
                    value={form.bucket}
                    disabled={!canManage}
                    placeholder="delivery"
                    onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm text-slate-600 dark:text-slate-300">
                    区域
                  </span>
                  <input
                    className={input}
                    value={form.region}
                    disabled={!canManage}
                    placeholder="R2 填 auto"
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                  />
                </label>
              </div>

              {/*
                ★ 密钥这两栏填的是**变量名**。
                  写清楚「不是密钥本身」,因为这个框长得就像该粘密钥的地方 ——
                  后端也会拒,但让人在粘之前就明白,比粘完被拒好。
              */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
                <p className="mb-2 text-sm text-amber-900 dark:text-amber-200">
                  下面两栏填的是<b>服务器上环境变量的名字</b>,不是密钥本身 ——
                  密钥能读写整桶照片,而照片里有门牌号和收件人姓名,它不进数据库
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-amber-900 dark:text-amber-200">
                      访问密钥的变量名
                    </span>
                    <input
                      className={input}
                      value={form.accessKeyRef}
                      disabled={!canManage}
                      placeholder="env:S3_ACCESS_KEY_ID"
                      onChange={(e) => setForm({ ...form, accessKeyRef: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-amber-900 dark:text-amber-200">
                      私有密钥的变量名
                    </span>
                    <input
                      className={input}
                      value={form.secretKeyRef}
                      disabled={!canManage}
                      placeholder="env:S3_SECRET_ACCESS_KEY"
                      onChange={(e) => setForm({ ...form, secretKeyRef: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={form.forcePathStyle}
                  disabled={!canManage}
                  onChange={(e) => setForm({ ...form, forcePathStyle: e.target.checked })}
                />
                路径式地址(MinIO 和多数自建服务要勾上,AWS S3 不用)
              </label>

              {/*
                ★ 换桶的后果要在按下去之前说,不是之后。
                  订单里存的是桶内路径 —— 换了桶,历史签收照片全部读不出来,
                  而那是事后唯一能证明「货交出去了」的东西。
              */}
              {bucketChanged && (
                <div
                  className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40
                             dark:text-red-300"
                  data-testid="bucket-change-warning"
                >
                  你正在换桶(<b>{settings.bucket}</b> → <b>{form.bucket || '(空)'}</b>)。
                  <b>已有的签收照片不会跟着搬过去</b>,换完就读不出来了 ——
                  除非你已经把旧桶的内容复制到新桶
                </div>
              )}

              {canManage && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void save()}
                    disabled={saving}
                    className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                               disabled:opacity-40"
                  >
                    {saving ? '保存中…' : confirmBucket ? '确认换桶并保存' : '保存'}
                  </button>
                  <button
                    onClick={() => void run()}
                    disabled={busy}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium
                               text-slate-700 disabled:opacity-40 dark:border-slate-600
                               dark:text-slate-200"
                  >
                    {busy ? '自检中…' : '测试存储'}
                  </button>
                  {saved && <span className="text-sm text-green-600">已保存</span>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {err && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {res && (
        <div className="mt-4 max-w-2xl space-y-3">
          <div
            className={`rounded-lg px-3 py-2 text-sm font-medium ${
              res.ok
                ? 'bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300'
                : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
            }`}
          >
            {res.ok ? '通了 —— 传得上、读得回、也删得掉' : '没通,看下面是哪一步断的'}
          </div>

          <dl className="grid grid-cols-[6rem_1fr] gap-y-1 text-sm">
            <dt className="text-slate-500">服务地址</dt>
            <dd className="break-all font-mono text-xs text-slate-700 dark:text-slate-300">
              {res.endpoint}
            </dd>
            <dt className="text-slate-500">桶</dt>
            <dd className="font-mono text-xs text-slate-700 dark:text-slate-300">{res.bucket}</dd>
            <dt className="text-slate-500">区域</dt>
            <dd className="font-mono text-xs text-slate-700 dark:text-slate-300">{res.region}</dd>
            <dt className="text-slate-500">来自</dt>
            <dd className="text-xs text-slate-700 dark:text-slate-300">
              {res.source === 'tenant' ? '后台配置' : '服务器环境变量'}
            </dd>
          </dl>

          {/*
            ★ 一步一步列出来,不是一句「失败」。
              凭证错、桶不存在、区域签错(R2 必须用 auto)、
              桶没配 CORS —— 四件事的处理方式完全不同,
              而它们都长成「传不上」。
          */}
          <ol className="space-y-1">
            {res.steps.map((s, i) => (
              <li
                key={`${s.step}-${i}`}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
              >
                <span className={s.ok ? 'text-green-600' : 'text-red-600'}>{s.ok ? '✓' : <InlineIcon icon={X} />}</span>{' '}
                <span className="text-slate-800 dark:text-slate-200">{s.step}</span>
                {s.detail && (
                  <div className="mt-0.5 break-words text-xs text-slate-500">{s.detail}</div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
